import { useState } from 'react';

import { startLogin, verifyLogin } from './authApi';
import { useAuthStore } from './authStore';

export function LoginScreen() {
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const setSession = useAuthStore((s) => s.setSession);

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await startLogin(email);
      setStage('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const session = await verifyLogin(email, code);
      setSession(session);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.shell}>
      <div style={styles.card}>
        <h1 style={styles.title}>Forge Builder</h1>
        <p style={styles.subtitle}>
          {stage === 'email' ? 'Sign in with email' : `Enter the 6-digit code we just sent to ${email}`}
        </p>
        {stage === 'email' ? (
          <form onSubmit={handleStart}>
            <input
              type="email"
              required
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
            />
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <input
              inputMode="numeric"
              required
              autoFocus
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ ...styles.input, letterSpacing: '0.4em', textAlign: 'center', fontFamily: 'monospace' }}
            />
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Verifying…' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => {
                setStage('email');
                setCode('');
              }}
              style={styles.linkButton}
            >
              Use a different email
            </button>
          </form>
        )}
        {error && <div style={styles.error}>{error}</div>}
        <p style={styles.hint}>
          Local dev: the OTP is logged to the auth-service container output —
          run <code>docker compose logs -f auth-service</code> to see it.
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    fontFamily: 'system-ui, sans-serif',
  },
  card: {
    width: 360,
    padding: 32,
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',
  },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#0F172A' },
  subtitle: { margin: '4px 0 24px', fontSize: 14, color: '#64748B' },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    boxSizing: 'border-box',
    marginBottom: 12,
  },
  button: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
  linkButton: {
    width: '100%',
    padding: '10px 12px',
    marginTop: 8,
    fontSize: 13,
    color: '#64748B',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  error: {
    marginTop: 12,
    padding: 8,
    fontSize: 12,
    color: '#991B1B',
    backgroundColor: '#FEE2E2',
    borderRadius: 6,
  },
  hint: { marginTop: 24, fontSize: 11, color: '#94A3B8' },
};
